import React, { createContext, useContext, useState } from "react";
import { createHashRouter, RouterProvider, useInRouterContext } from "react-router-dom";
import { reactRouterProvider } from "ra-core";

/**
 * Context used to thread changing Admin children into a long-lived HashRouter.
 *
 * Problem this solves:
 *   ra-core's default InternalRouter calls `createHashRouter()` unconditionally
 *   on every render. When the Admin children (resource list) change – e.g. when
 *   switching between app modules – a brand-new router is created. ReactRouter's
 *   RouterProvider stores the router state in useState(router.state), which only
 *   initialises once on mount. The new router's async initialNavigation fires
 *   *after* RouterProvider has subscribed (in useLayoutEffect), but resource-list
 *   effects (useEffect) may run concurrently, leaving a brief window where the
 *   *old* location (e.g. "/raza-events/123") is rendered against the *new* resource
 *   set (global-only resources) → no matching route → CatchAll → "Not Found" flash.
 *
 * Solution:
 *   Keep the same createHashRouter instance alive for the lifetime of the page.
 *   Thread changing children through a React context so the route subtree still
 *   updates whenever the resource list changes, without recreating the router.
 */
const ChildrenBridgeContext = createContext<React.ReactNode>(null);

const ChildrenBridgeConsumer: React.FC = () => <>{useContext(ChildrenBridgeContext)}</>;

const StableInternalRouter: React.FC<{
  basename?: string;
  children: React.ReactNode;
}> = ({ basename = "", children }) => {
  const [router] = useState(() =>
    createHashRouter([{ path: "*", element: <ChildrenBridgeConsumer /> }], {
      basename,
    })
  );

  return (
    <ChildrenBridgeContext.Provider value={children}>
      <RouterProvider router={router} />
    </ChildrenBridgeContext.Provider>
  );
};

const StableRouterWrapper: React.FC<{
  basename?: string;
  children: React.ReactNode;
}> = ({ basename, children }) => {
  const isInRouter = useInRouterContext();
  if (isInRouter) {
    return <>{children}</>;
  }
  return <StableInternalRouter basename={basename}>{children}</StableInternalRouter>;
};

/**
 * A react-admin routerProvider identical to the default one shipped by ra-core,
 * except that its RouterWrapper keeps the same HashRouter instance across
 * renders.  Pass this to `<Admin routerProvider={stableRouterProvider}>` to
 * prevent the "Not Found" flash when switching between admin modules.
 */
export const stableRouterProvider = {
  ...reactRouterProvider,
  RouterWrapper: StableRouterWrapper,
};
